import React, { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import useLockBodyScroll from "../../../hooks/useLockBodyScroll";

const initials = (employee) =>
  `${employee.first_name ? employee.first_name[0].toUpperCase() : ""}${
    employee.last_name ? employee.last_name[0].toUpperCase() : ""
  }`;

const EmployeePopup = ({
  employees,
  assignedEmployeeIds = [],
  maxSelected,
  onConfirm,
  onClose,
  saving,
  // "modal" (default): fixed-position overlay + centered/bottom-sheet card,
  // used on Mobile. "panel": same header/list/footer content rendered
  // inline (no overlay, no fixed sizing) so it can drop directly into the
  // Tablet/Desktop persistent detail panel (TimeslotsSection.jsx) instead
  // of opening as a popup. Selection/save logic below is identical either
  // way — only the outer wrapper differs.
  variant = "modal",
}) => {
  const [selectedIds, setSelectedIds] = useState(
    () => new Set(assignedEmployeeIds)
  );
  const [limitHit, setLimitHit] = useState(false);
  // Only the "modal" variant is an overlay popup — "panel" renders inline with no overlay, so
  // it shouldn't lock the page (it's not covering/blocking anything).
  useLockBodyScroll(variant === "modal");

  const toggleEmployee = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        setLimitHit(false);
      } else {
        if (next.size >= maxSelected) {
          setLimitHit(true);
          return prev;
        }
        setLimitHit(false);
        next.add(id);
      }
      return next;
    });
  };

  const hasChanges =
    selectedIds.size !== assignedEmployeeIds.length ||
    assignedEmployeeIds.some((id) => !selectedIds.has(id));

  const content = (
    <>
      <div className="hf-tm-emp-header">
        <h2 className="hf-tm-emp-title">Uredi zaposlene za termin</h2>
        <button
          type="button"
          className="hf-tm-emp-close"
          onClick={onClose}
          aria-label="Zatvori"
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </button>
      </div>

      <div className="hf-tm-emp-counter">
        Izabrano {selectedIds.size}/{maxSelected}
      </div>

      <div className="hf-tm-emp-list">
        {employees.length === 0 && (
          <div className="hf-tm-emp-empty">Nema dostupnih trenera</div>
        )}
        {employees.map((employee) => {
          const isSelected = selectedIds.has(employee.id);
          const isDisabled = !isSelected && selectedIds.size >= maxSelected;
          return (
            <div
              key={employee.id}
              className={`hf-tm-emp-row${
                isSelected ? " hf-tm-emp-row-selected" : ""
              }${isDisabled ? " hf-tm-emp-row-disabled" : ""}`}
              onClick={() => toggleEmployee(employee.id)}
            >
              <span className="hf-tm-emp-avatar">{initials(employee)}</span>
              <span className="hf-tm-emp-name">
                {employee.first_name} {employee.last_name}
              </span>
              <input
                type="checkbox"
                className="hf-tm-emp-check"
                readOnly
                checked={isSelected}
              />
            </div>
          );
        })}
      </div>

      {limitHit && (
        <div className="hf-tm-emp-limit-hint">
          Maksimalno {maxSelected} trenera po terminu
        </div>
      )}

      <div className="hf-tm-emp-footer">
        <button
          type="button"
          className="hf-tm-emp-cancel"
          onClick={onClose}
          disabled={saving}
        >
          Otkaži
        </button>
        <button
          type="button"
          className="hf-tm-emp-confirm"
          disabled={!hasChanges || saving}
          onClick={() => onConfirm(Array.from(selectedIds))}
        >
          Sačuvaj
        </button>
      </div>
    </>
  );

  if (variant === "panel") {
    return <div className="hf-tm-emp-panel-body">{content}</div>;
  }

  return (
    <div
      className="hf-tm-emp-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="hf-tm-emp-modal">{content}</div>
    </div>
  );
};

export default EmployeePopup;
